import { ReparoApi } from './index';

/**
 * Selling to B2B customers (distributors, retail chains, institutions). Brand side: /b2b/*.
 * The customer portal has its own client (b2bPortal.ts). Orders are ordinary Sales Orders; these
 * calls cover the steps after submission. Docs: docs/prds/distributor-order-portal-*.md.
 */

/* ---------------------------------- Types --------------------------------- */

export type B2bOrderStatus =
    | 'Submitted'
    | 'PI Issued'
    | 'PI Confirmed'
    | 'Payment Submitted'
    | 'Payment Done'
    | 'Shipped'
    | 'Fulfilled'
    | 'Cancelled';

export interface PaymentTermTemplate {
    label: string;
    type: 'ADVANCE' | 'BALANCE' | 'CREDIT' | 'CUSTOM';
    percentage: number;
    anchor: string;
    offsetDays?: number;
}

export interface B2bSegment {
    key: string;
    label: string;
    defaultTerms?: PaymentTermTemplate[];
    minOrderValue?: number;
}

export interface B2bRecipients {
    to: string[];
    cc: string[];
}

export interface B2bConfig {
    enabled: boolean;
    segments: B2bSegment[];
    channelKeys: string[];
    instructions?: string;
    piValidityDays: number;
    paymentVerifySlaHours: number;
    defaultShipFromWarehouseId?: string;
    /** The brand's WMS records dispatches, so recording one here books no stock. */
    dispatchRecordedInWms?: boolean;
    notifications: { orderPlaced?: B2bRecipients; paymentSubmitted?: B2bRecipients; dispatched?: B2bRecipients };
    /** Columns such as Size or Colour, each read from a product field or a custom attribute. */
    productDetails: ProductDetailSetting[];
}

export type ProductDetailField = 'category' | 'subCategory' | 'productGroup' | 'productType';
export interface ProductDetailSetting {
    label: string;
    source: { field: ProductDetailField } | { attributeId: string };
}

/** One product detail as shown, e.g. { label: 'Size', value: 'XL' }. */
export interface ProductDetail {
    label: string;
    value: string | null;
}

/** What each product field and custom attribute holds, for the Product details picker. */
export interface ProductDetailSources {
    total: number;
    fields: Array<{ field: ProductDetailField; filled: number; examples: string[] }>;
    attributes: Array<{ id: string; name: string; filled: number; examples: string[] }>;
    /** A real product's values by source key ('field:subCategory', 'attr:<id>'). */
    sample: { productId: string; values: Record<string, string | null> } | null;
}

export interface B2bShipTo {
    warehouseId: string;
    name: string;
    address: string;
    gstin: string | null;
    stateCode: string | null;
}

export interface B2bCustomer {
    merchantId: string;
    customerId: string;
    name: string;
    legalName: string | null;
    gstin: string | null;
    email: string | null;
    segment: string;
    segmentLabel: string | null;
    tags: string[];
    /** ACTIVE customers can order; BLOCKED can log in and see orders only. */
    status: 'ACTIVE' | 'BLOCKED' | 'INACTIVE';
    billingAddress: string;
    shipTos: B2bShipTo[];
    /** The brand's B2B channel this customer's orders are placed on. */
    channelKey: string | null;
    shipFromWarehouseId: string | null;
    minOrderValue: number;
    paymentTerms: PaymentTermTemplate[];
}

export interface B2bAddressInput {
    street: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
}

export interface CreateB2bCustomerInput {
    name: string;
    legalName?: string;
    gstin?: string;
    segment: string;
    tags?: string[];
    email: string;
    phone?: string;
    billing: B2bAddressInput;
    shipTos: Array<B2bAddressInput & { name: string; gstin?: string }>;
    shipFromWarehouseId?: string;
    minOrderValue?: number;
    paymentTerms?: PaymentTermTemplate[];
}

export type PriceSource = 'CUSTOMER' | 'SEGMENT' | 'DEFAULT' | 'OVERRIDE';

export interface QuotedLine {
    productId: string;
    sku: string;
    name: string;
    category: string | null;
    /** The brand's product details (e.g. Size, Colour), as set in B2B settings. */
    details: ProductDetail[];
    mrp: number | null;
    listPrice: number;
    unitPrice: number;
    priceSource: PriceSource;
    gstRate: number | null;
    hsnCode: string | null;
    quantity: number;
    lineTotal: number;
    lineItemId?: string;
    free?: boolean;
    schemeCode?: string;
}

export interface OrderCharge {
    label: string;
    amount: number;
    kind?: string;
    schemeCode?: string;
}

export interface SchemeEntitlement {
    schemeCode: string;
    schemeName: string;
    units: number;
    pool: { type: 'ANY' | 'SAME_ITEM' | 'LIST'; productIds: string[] };
}

export interface OrderQuote {
    lines: QuotedLine[];
    freeLines: QuotedLine[];
    schemes: {
        applied: Array<{ code: string; name: string; achievement: number }>;
        rejected: Array<{ code: string; name: string; reason: string }>;
        entitlements: SchemeEntitlement[];
    };
    charges: OrderCharge[];
    totals: { subtotal: number; taxAmount: number; total: number; payable: number };
    minOrderValue: number;
    errors: string[];
}

export interface OrderLineInput {
    productId: string;
    quantity: number;
}

export interface ReviewLineInput extends OrderLineInput {
    unitPrice?: number;
    priceReason?: string;
}

export interface B2bScheme {
    id: string;
    code: string;
    versionNo: number;
    name: string;
    status: 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'CLOSED';
    effectiveFrom: string;
    effectiveTo: string | null;
    appliesToAll: boolean;
    customerIds: string[];
    segments: string[];
    tags: string[];
    priority: number;
    exclusivity: 'GLOBAL' | 'CLASS' | 'NONE';
    slabBasis: 'QTY' | 'VALUE';
    benefitType: 'FREE_QTY' | 'PERCENT' | 'AMOUNT';
    rule: {
        slabs: Array<{ min: number; max: number | null; buy?: number; get?: number; value?: number }>;
        productScope?: { categories: string[]; productIds: string[] };
        freePool?: { type: 'ANY' | 'SAME_ITEM' | 'LIST'; productIds: string[] };
    };
}

export type SchemeDraftInput = Omit<B2bScheme, 'id' | 'versionNo' | 'status' | 'effectiveTo'> & { effectiveTo?: string };

export interface B2bCatalog {
    products: QuotedLine[];
    schemes: B2bScheme[];
    minOrderValue: number;
    instructions: string | null;
}

export interface B2bPrice {
    ownerKey: string;
    productId: string;
    /** Null only on a Not offered row that has no price of its own. */
    price: number | null;
    /** Not offered: takes the product away from this customer or segment. */
    hidden: boolean;
    priceEffectiveFrom: string | null;
    /** Earlier states, oldest first: the price (null = none on this list), whether it was offered, when, and who set it. */
    priceHistory: Array<{ price: string | null; hidden?: boolean; from: string | null; to: string; by?: string | null }>;
    updatedBy: string | null;
}

export interface PriceCsvResult {
    totalRows: number;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; reason: string; sku?: string }>;
    dryRun: boolean;
}

export interface PaymentRecord {
    id: string;
    amount: number;
    paidOn: string;
    method: string;
    reference: string;
    note?: string | null;
    status?: 'SUBMITTED' | 'VERIFIED' | 'REJECTED';
    proofKeys?: string[];
    verifyBy?: string;
    verifiedBy?: string;
    verifiedAt?: string;
    rejectionReason?: string;
    rejectionNote?: string;
    overdue: boolean;
    duplicateOf: string[];
}

export interface PaymentView {
    payable: number;
    dueBeforeDispatch: number;
    settled: number;
    pendingVerification: number;
    outstanding: number;
    payments: PaymentRecord[];
}

export interface DispatchDocument {
    kind: 'TAX_INVOICE' | 'LR_COPY' | 'EWAY_BILL' | 'PACKING_LIST' | 'OTHER';
    fileKey: string;
    fileName: string;
}

export interface DispatchDetails {
    transporterName: string;
    trackingNumber: string;
    dispatchDate: string;
    invoiceNumber: string;
    invoiceDate: string;
    invoiceAmount?: number;
    ewayBillNumber?: string;
    documents: DispatchDocument[];
    recordedBy: string;
    recordedAt: string;
}

export interface B2bDispatch {
    id: string;
    code: string;
    status: 'DRAFT' | 'APPROVED' | 'DISPATCHED' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';
    fromNodeId: string | null;
    fromNodeName?: string | null;
    dispatchedAt: string | null;
    receivedAt: string | null;
    dispatchDetails: DispatchDetails | null;
    items: Array<{ id: string; itemId: string; plannedQty: number; dispatchedQty: number | null; receivedQty: number | null }>;
}

export interface OrderDispatches {
    balance: Array<{ productId: string; ordered: number; onDispatches: number; remaining: number }>;
    dispatches: B2bDispatch[];
}

export interface B2bOrderItem {
    itemId: string;
    lineItemId: string;
    description?: string | null;
    quantity: number;
    unitPrice: number | null;
    gstRate: number | null;
    lineTotal: number | null;
    status: string | null;
    cancellationReason: string | null;
}

export interface B2bOrder {
    purchaseOrderId: string;
    channelId: string;
    b2bCustomerId: string;
    vendorName: string | null;
    status: B2bOrderStatus;
    date: string;
    expiryDate: string | null;
    warehouseId: string | null;
    shipToName: string | null;
    shipToAddress: string | null;
    poRefNum: string | null;
    /** When the latest PI was emailed, and to whom. */
    sentAt: string | null;
    sentTo: string | null;
    notes: string | null;
    /** Printed on the PI; copied from the brand's standard terms when the order was placed. */
    termsAndConditions: string | null;
    subtotal: number;
    taxAmount: number;
    total: number;
    additionalCharges: OrderCharge[] | null;
    items: B2bOrderItem[];
}

export interface B2bOrderDetail {
    order: B2bOrder;
    availability: Array<{ productId: string; onHand: number; reservedElsewhere: number; available: number }>;
    payments: PaymentView;
    dispatches: OrderDispatches;
    history: Array<{ status: string; at: string }>;
    files: string[];
    /** The order's audit records (placed, reviews, cancelled, closed short), for Activity. */
    audit: B2bAuditEntry[];
}

export interface B2bAuditEntry {
    kind: 'PLACED' | 'REVIEWED' | 'PI_CONFIRMED' | 'CANCELLED' | 'CLOSED_SHORT';
    at: string;
    by: string | null;
    onBehalf?: boolean;
    lines?: number;
    units?: number;
    payable?: number;
    changes?: Array<{
        productId: string;
        change: 'ADDED' | 'CHANGED' | 'REMOVED';
        before?: { quantity: number; unitPrice: number };
        after?: { quantity: number; unitPrice: number };
        reason?: string | null;
    }>;
    totalBefore?: number;
    totalAfter?: number;
    reason?: string;
    onBehalfOfCustomer?: boolean;
    note?: string | null;
    fileKey?: string | null;
}

/** A real document of the order (PI, proof, dispatch paperwork), not one of its JSON audit records. */
export const isOrderDocument = (key: string): boolean => !key.endsWith('.json');

export interface DispatchForm {
    transporterName: string;
    trackingNumber: string;
    dispatchDate: string;
    invoiceNumber: string;
    invoiceDate: string;
    invoiceAmount?: number;
    ewayBillNumber?: string;
}

export const PAYMENT_REJECTION_REASONS: Array<{ value: string; label: string }> = [
    { value: 'AMOUNT_MISMATCH', label: 'Amount does not match' },
    { value: 'UTR_NOT_FOUND', label: 'UTR not found in our account' },
    { value: 'PROOF_UNREADABLE', label: 'Proof unreadable' },
    { value: 'WRONG_ACCOUNT', label: 'Paid to the wrong account' },
    { value: 'DUPLICATE_UTR', label: 'UTR already used on another order' },
    { value: 'OTHER', label: 'Other' },
];

/* ---------------------------------- Calls --------------------------------- */

const json = (body: unknown) => JSON.stringify(body);

export const getB2bConfig = () => ReparoApi<B2bConfig>({ method: 'GET', url: '/b2b/config' });
export const getB2bProductDetailSources = () => ReparoApi<ProductDetailSources>({ method: 'GET', url: '/b2b/product-detail-sources' });
export const updateB2bConfig = (patch: Partial<B2bConfig>) =>
    ReparoApi<B2bConfig>({ method: 'PATCH', url: '/b2b/config', body: json(patch) });

export const listB2bCustomers = () => ReparoApi<B2bCustomer[]>({ method: 'GET', url: '/b2b/customers' });
export const getB2bCustomer = (customerId: string) =>
    ReparoApi<B2bCustomer>({ method: 'GET', url: `/b2b/customers/${encodeURIComponent(customerId)}` });
export const createB2bCustomer = (input: CreateB2bCustomerInput) =>
    ReparoApi<B2bCustomer>({ method: 'POST', url: '/b2b/customers', body: json(input) });
export type B2bCustomerStatus = 'ACTIVE' | 'BLOCKED' | 'INACTIVE';

/** One saved edit of a customer: who, when, each field before and after. */
export interface B2bCustomerChange {
    at: string;
    by: string;
    changes: Array<{ field: string; before: string; after: string }>;
}

/** A customer as stored, for the edit form in Settings (no segment fallbacks applied). */
export interface B2bCustomerEditable {
    customerId: string;
    name: string;
    legalName: string | null;
    gstin: string | null;
    email: string | null;
    phone: string | null;
    billing: B2bAddressInput & { country: string };
    segment: string;
    tags: string[];
    status: B2bCustomerStatus;
    paymentTerms: PaymentTermTemplate[];
    minOrderValue: number | null;
    shipFromWarehouseId: string | null;
    shipTos: Array<B2bAddressInput & { warehouseId: string; name: string; country: string; gstin: string | null }>;
    history: B2bCustomerChange[];
}

export interface UpdateB2bCustomerInput extends Omit<CreateB2bCustomerInput, 'shipTos'> {
    status: B2bCustomerStatus;
    /** `warehouseId` for an existing location; absent for a new one. Left-out locations are removed. */
    shipTos: Array<B2bAddressInput & { name: string; gstin?: string; warehouseId?: string }>;
}

export const getB2bCustomerForEdit = (customerId: string) =>
    ReparoApi<B2bCustomerEditable>({ method: 'GET', url: `/b2b/customers/${encodeURIComponent(customerId)}/edit` });
export const updateB2bCustomer = (customerId: string, input: UpdateB2bCustomerInput) =>
    ReparoApi<B2bCustomerEditable>({ method: 'PUT', url: `/b2b/customers/${encodeURIComponent(customerId)}`, body: json(input) });
export const inviteB2bCustomer = (customerId: string, input: { email: string; firstName?: string; lastName?: string }) =>
    ReparoApi<{ link: string; emailed: boolean }>({
        method: 'POST',
        url: `/b2b/customers/${encodeURIComponent(customerId)}/invite`,
        body: json(input),
    });
export const getB2bCustomerCatalog = (customerId: string) =>
    ReparoApi<B2bCatalog>({ method: 'GET', url: `/b2b/customers/${encodeURIComponent(customerId)}/catalog` });

export interface B2bProduct {
    productId: string;
    sku: string;
    name: string;
    category: string | null;
    /** The brand's product details (e.g. Size, Colour), as set in B2B settings. */
    details: ProductDetail[];
    mrp: number | null;
    gstRate: number | null;
}

export const listB2bProducts = () => ReparoApi<B2bProduct[]>({ method: 'GET', url: '/b2b/products' });
export const listB2bCustomerOrders = (customerId: string) =>
    ReparoApi<Array<{ purchaseOrderId: string; date: string; status: B2bOrderStatus; total: number; customerReference: string | null; expiryDate: string | null }>>({
        method: 'GET',
        url: `/b2b/customers/${encodeURIComponent(customerId)}/orders`,
    });

/** `owner`: DEFAULT, SEGMENT:<key> or CUSTOMER:<customerId>. */
export const listB2bPrices = (owner: string) =>
    ReparoApi<B2bPrice[]>({ method: 'GET', url: `/b2b/prices?owner=${encodeURIComponent(owner)}` });
export const upsertB2bPrices = (owner: string, rows: Array<{ productId: string; price: number }>) =>
    ReparoApi<{ created: number; updated: number; unchanged: number }>({ method: 'PUT', url: '/b2b/prices', body: json({ owner, rows }) });
export const setB2bPricesOffered = (owner: string, productIds: string[], offered: boolean) =>
    ReparoApi<{ changed: number }>({ method: 'PUT', url: '/b2b/prices/offered', body: json({ owner, productIds, offered }) });
export const removeB2bPrice = (owner: string, productId: string) =>
    ReparoApi<void>({ method: 'DELETE', url: `/b2b/prices/${encodeURIComponent(productId)}?owner=${encodeURIComponent(owner)}` });
export const uploadB2bPrices = (owner: string, file: File, dryRun: boolean) => {
    const form = new FormData();
    form.append('file', file);
    form.append('owner', owner);
    form.append('dryRun', String(dryRun));
    return ReparoApi<PriceCsvResult>({
        method: 'POST',
        url: '/b2b/prices/upload',
        body: form,
    });
};

export const listB2bSchemes = (includeEnded = false) =>
    ReparoApi<B2bScheme[]>({ method: 'GET', url: `/b2b/schemes?includeEnded=${includeEnded}` });
export const createB2bScheme = (input: SchemeDraftInput) =>
    ReparoApi<B2bScheme>({ method: 'POST', url: '/b2b/schemes', body: json(input) });
export const updateB2bScheme = (id: string, input: SchemeDraftInput) =>
    ReparoApi<B2bScheme>({ method: 'PUT', url: `/b2b/schemes/${id}`, body: json(input) });
export const newB2bSchemeVersion = (id: string) => ReparoApi<B2bScheme>({ method: 'POST', url: `/b2b/schemes/${id}/versions` });
export const publishB2bScheme = (id: string) => ReparoApi<B2bScheme>({ method: 'POST', url: `/b2b/schemes/${id}/publish` });
export const closeB2bScheme = (id: string, effectiveTo: string) =>
    ReparoApi<B2bScheme>({ method: 'POST', url: `/b2b/schemes/${id}/close`, body: json({ effectiveTo }) });
export const deleteB2bScheme = (id: string) => ReparoApi<void>({ method: 'DELETE', url: `/b2b/schemes/${id}` });

export const quoteB2bOrder = (
    customerId: string,
    lines: ReviewLineInput[],
    freePicks?: OrderLineInput[],
    freeOverride?: { lines: OrderLineInput[]; note: string },
) => ReparoApi<OrderQuote>({ method: 'POST', url: '/b2b/orders/quote', body: json({ customerId, lines, freePicks, freeOverride }) });
export const placeB2bOrderOnBehalf = (input: {
    customerId: string;
    lines: OrderLineInput[];
    freePicks?: OrderLineInput[];
    shipToWarehouseId?: string;
    customerReference?: string;
    notes?: string;
}) => ReparoApi<B2bOrder>({ method: 'POST', url: '/b2b/orders', body: json(input) });

const order = (id: string) => `/b2b/orders/${encodeURIComponent(id)}`;

export const getB2bOrder = (id: string) => ReparoApi<B2bOrderDetail>({ method: 'GET', url: order(id) });
export const reviewB2bOrder = (
    id: string,
    input: { lines: ReviewLineInput[]; freePicks?: OrderLineInput[]; freeOverride?: { lines: OrderLineInput[]; note: string }; termsAndConditions?: string | null },
) => ReparoApi<B2bOrder>({ method: 'POST', url: `${order(id)}/review`, body: json(input) });
/** The PI as issuing would render it (HTML), nothing saved or sent; `draft` previews unsaved review edits. */
export const previewB2bPi = (
    id: string,
    draft?: { lines: ReviewLineInput[]; freePicks?: OrderLineInput[]; freeOverride?: { lines: OrderLineInput[]; note: string }; termsAndConditions?: string | null },
) =>
    ReparoApi<{ html: string; revision: number; validTill: string; to: string[]; cc: string[]; errors: string[] }>({
        method: 'POST',
        url: `${order(id)}/pi/preview`,
        body: json(draft ? { draft } : {}),
    });
/** Issues the PI: emailed to `to` / `cc` as confirmed in the send dialog, or to nobody with `withoutEmail`. */
export const issueB2bPi = (id: string, send: { to?: string[]; cc?: string[]; withoutEmail?: boolean } = {}) =>
    ReparoApi<{ revision: number; fileKey: string; sentTo: string | null }>({ method: 'POST', url: `${order(id)}/pi`, body: json(send) });
/** The customer confirmed the PI off the portal: an admin records how, optionally with a screenshot. */
export const confirmB2bPiOnBehalf = (id: string, note: string, file?: File | null) => {
    const form = new FormData();
    form.append('note', note);
    if (file) form.append('file', file);
    return ReparoApi<{ ok: boolean }>({ method: 'POST', url: `${order(id)}/confirm-pi`, body: form });
};
export const cancelB2bOrder = (id: string, reason: string) =>
    ReparoApi<{ ok: boolean }>({ method: 'POST', url: `${order(id)}/cancel`, body: json({ reason }) });
export const verifyB2bPayment = (id: string, paymentId: string) =>
    ReparoApi<PaymentView>({ method: 'POST', url: `${order(id)}/payments/${paymentId}/verify` });
export const rejectB2bPayment = (id: string, paymentId: string, reason: string, note?: string) =>
    ReparoApi<PaymentView>({ method: 'POST', url: `${order(id)}/payments/${paymentId}/reject`, body: json({ reason, note }) });
export const createB2bDispatch = (id: string, input: { fromWarehouseId?: string; lines: OrderLineInput[]; notes?: string }) =>
    ReparoApi<B2bDispatch>({ method: 'POST', url: `${order(id)}/dispatches`, body: json(input) });
export const recordB2bDispatch = (id: string, stnId: string, form: DispatchForm, files: Array<{ kind: DispatchDocument['kind']; file: File }>) => {
    const body = new FormData();
    for (const [key, value] of Object.entries(form)) {
        if (value !== undefined && value !== null && value !== '') body.append(key, String(value));
    }
    for (const f of files) body.append('files', f.file);
    if (files.length) body.append('kinds', files.map((f) => f.kind).join(','));
    return ReparoApi<B2bDispatch>({ method: 'POST', url: `${order(id)}/dispatches/${stnId}/record`, body });
};
export const cancelB2bDispatch = (id: string, stnId: string) =>
    ReparoApi<B2bDispatch>({ method: 'POST', url: `${order(id)}/dispatches/${stnId}/cancel` });
export const closeB2bOrderShort = (id: string, reason: string) =>
    ReparoApi<OrderDispatches>({ method: 'POST', url: `${order(id)}/close-short`, body: json({ reason }) });
export const getB2bOrderFileUrl = (id: string, key: string) =>
    ReparoApi<{ url: string }>({ method: 'GET', url: `${order(id)}/file?key=${encodeURIComponent(key)}` });
/** The dispatch's packing list (Excel), built fresh on each download. */
export const getB2bPackingListUrl = (id: string, stnId: string) =>
    ReparoApi<{ url: string }>({ method: 'GET', url: `${order(id)}/dispatches/${stnId}/packing-list` });

/** What a file key is, for lists: "PI rev 2", "Payment proof", "Tax invoice". */
export const describeOrderFile = (key: string): string => {
    const name = key.split('/').pop() ?? key;
    const pi = /^pi-rev(\d+)\.pdf$/.exec(name);
    if (pi) return `Proforma invoice, revision ${pi[1]}`;
    if (key.includes('/proofs/')) return `Payment proof: ${name}`;
    const packingList = /^packing-list-(.+)\.xlsx$/.exec(name);
    if (packingList) return `Packing list, ${packingList[1]}`;
    if (key.includes('/dispatch/')) return `Dispatch document: ${name}`;
    if (key.includes('/confirmations/')) return `PI confirmation: ${name}`;
    if (/^review-\d+\.json$/.test(name)) return `Review record ${name.replace(/\D/g, '')}`;
    if (name === 'submitted.json') return 'Order as submitted';
    return name;
};
